import { AppIcon } from '@/components/ui/app-icon';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { UpgradeLink as UpgradeFlowLink } from '@/components/upgrade/upgrade-link.component';
import {
  iconEllipsis,
  iconLock,
  iconPlus,
  iconSpinnerThird,
} from '@/lib/icons';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

/**
 * One integration on the Integrations tab: a compact single-row card, in the
 * look of Gumloop's Connectors grid, every one the same height. `id` becomes
 * the card's `data-integration`, which ⌘K scrolls to (focusIntegrationCard).
 */
export function IntegrationCard({
  id,
  logo,
  name,
  badge,
  description,
  action,
  onConfigure,
  configureLabel = `Open ${name} connection`,
}: {
  id: string;
  logo: ReactNode;
  name: string;
  badge?: ReactNode;
  description: ReactNode;
  action?: ReactNode;
  onConfigure?: () => void;
  configureLabel?: string;
}) {
  return (
    <div
      data-integration={id}
      className={cn(
        'relative flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-2.5',
        onConfigure && 'transition-colors hover:border-primary/40'
      )}
    >
      {onConfigure && (
        <button
          type='button'
          aria-label={configureLabel}
          title={typeof description === 'string' ? description : undefined}
          onClick={onConfigure}
          className='absolute inset-0 cursor-pointer rounded-xl focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none'
        />
      )}
      <div className='pointer-events-none relative flex size-8 shrink-0 items-center justify-center rounded-lg border border-border bg-background'>
        {logo}
      </div>
      <div className='pointer-events-none relative min-w-0 flex-1'>
        {/* A fixed height, so a card with a pill is as tall as one without. */}
        <p className='flex h-5 min-w-0 items-center gap-1.5'>
          <span className='truncate text-base font-medium text-foreground'>
            {name}
          </span>
          {badge}
        </p>
        <div
          className='truncate text-xs text-muted-foreground'
          title={typeof description === 'string' ? description : undefined}
        >
          {description}
        </div>
      </div>
      <div
        className={cn(
          'flex shrink-0 items-center gap-1.5',
          onConfigure && 'relative'
        )}
      >
        {action}
      </div>
    </div>
  );
}

/** Existing connection actions for providers without a dedicated settings page. */
export function IntegrationConnectionDialog({
  name,
  open,
  onOpenChange,
  description,
  children,
}: {
  name: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  description: ReactNode;
  children: ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>{name} connection</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className='flex flex-wrap items-center gap-2'>{children}</div>
      </DialogContent>
    </Dialog>
  );
}

// Every pill on the tab has the Pro pill's shape.
const PILL =
  'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] leading-4 font-semibold ring-1 ring-inset';

const TONES = {
  success: 'bg-success/10 text-success ring-success/20',
  warning: 'bg-warning/10 text-warning ring-warning/20',
  muted: 'bg-muted text-muted-foreground ring-border',
};

/** A status pill: after the name (Reconnect needed, Setup pending…), or on
 * the right of a connected card. */
export function StatusPill({
  tone,
  children,
}: {
  tone: keyof typeof TONES;
  children: string;
}) {
  return (
    <span className={cn(PILL, TONES[tone])}>
      <span className='size-1.5 rounded-full bg-current' />
      {children}
    </span>
  );
}

export const ConnectedPill = () => (
  <StatusPill tone='success'>Connected</StatusPill>
);

/** A card's small text action: Reconnect, Retry status and the like. */
export const CARD_BUTTON = 'h-7 rounded-md px-2.5 text-xs';

// Stretches a card's only action over the card, so the whole card is its
// target (and hovering the card lights the action up).
const STRETCH = 'before:absolute before:inset-0 before:rounded-xl';

/** The outlined "+" that connects. */
export function ConnectButton({
  name,
  onClick,
  disabled,
  busy,
}: {
  name: string;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <Button
      variant='outline'
      size='icon'
      aria-label={`Connect ${name}`}
      title={`Connect ${name}`}
      onClick={onClick}
      disabled={disabled || busy}
      className={cn(
        'h-7 w-7 cursor-pointer rounded-md text-muted-foreground hover:text-foreground [&_svg]:size-3',
        STRETCH
      )}
    >
      <AppIcon icon={busy ? iconSpinnerThird : iconPlus} spin={busy} />
    </Button>
  );
}

/** A dropped connection's way back in. */
export function ReconnectButton({
  name,
  onClick,
  disabled,
  busy,
}: {
  name: string;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <Button
      variant='outline'
      size='sm'
      aria-label={`Reconnect ${name}`}
      onClick={onClick}
      disabled={disabled || busy}
      className={CARD_BUTTON}
    >
      {busy ? <AppIcon icon={iconSpinnerThird} spin /> : 'Reconnect'}
    </Button>
  );
}

/** A set-up card's one "⋯" button, holding its manage actions
 * (DropdownMenuItems). */
export function ManageMenu({
  name,
  children,
}: {
  name: string;
  children: ReactNode;
}) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          size='icon'
          aria-label={`Manage ${name}`}
          title={`Manage ${name}`}
          className='h-7 w-7 rounded-md text-muted-foreground hover:text-foreground [&_svg]:size-3.5'
        >
          <AppIcon icon={iconEllipsis} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='min-w-40'>
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** A Disconnect menu item's look. */
export const DESTRUCTIVE_ITEM = 'text-destructive focus:text-destructive';

/**
 * The "Pro" lock pill. Every few seconds a soft light sweeps across it (the
 * What's new button's shine, motion-safe only), so what needs Pro catches the
 * eye without shouting.
 */
export function ProPill() {
  return (
    <span
      className={cn(
        PILL,
        'relative overflow-hidden bg-primary/10 text-primary ring-primary/20 transition-colors after:pointer-events-none after:absolute after:inset-y-0 after:left-0 after:hidden after:w-[45%] after:bg-linear-to-r after:from-transparent after:via-white/70 after:to-transparent motion-safe:after:block motion-safe:after:animate-[whats-new-shine_6s_ease-in-out_infinite] dark:after:via-white/20'
      )}
    >
      <AppIcon icon={iconLock} style={{ fontSize: 9 }} />
      Pro
    </span>
  );
}

/** A locked integration's action: the Pro pill, to the upgrade wizard on Pro.
 * The whole card links. */
export function UpgradeLink({ name }: { name: string }) {
  return (
    <UpgradeFlowLink
      plan='pro'
      source='integrations'
      aria-label={`Upgrade to Pro to connect ${name}`}
      title='Upgrade to Pro'
      className={cn(
        'inline-flex rounded-full focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none [&:hover>span]:bg-primary/15',
        STRETCH
      )}
    >
      <ProPill />
    </UpgradeFlowLink>
  );
}
